// Shared error state (#139) — a real failure, announced as an alert and
// recoverable in place. Deliberately DISTINCT from EmptyState: a network error is
// not an empty account (LookLibrary used to collapse fetch errors into "empty",
// hiding the difference and offering no retry). Pass `onRetry` to re-run the fetch.

export interface ErrorStateProps {
  title?: string;
  /** A human cause, e.g. "Couldn't reach the server." */
  message?: React.ReactNode;
  /** Optional in-place recovery — renders a Retry button that re-runs the failed action. */
  onRetry?: () => void;
  retryLabel?: string;
  /**
   * Heading level for the title (default 2 — a top-level page state). Pass a deeper level when
   * the error sits INSIDE a section so the document outline stays ordered (e.g. 4 under an h3
   * panel like LookLibrary — an h2 there would invert the heading order).
   */
  headingLevel?: 2 | 3 | 4;
}

export function ErrorState({
  title = "Something went wrong",
  message,
  onRetry,
  retryLabel = "Try again",
  headingLevel = 2,
}: ErrorStateProps): React.JSX.Element {
  const Heading = `h${headingLevel}` as "h2" | "h3" | "h4";
  return (
    <div className="empty empty--error" role="alert" data-testid="error-state">
      <div className="empty__art" aria-hidden="true">
        !
      </div>
      <Heading className="empty__title">{title}</Heading>
      {message != null && <p className="empty__body">{message}</p>}
      {onRetry && (
        <button
          type="button"
          className="btn empty__cta"
          onClick={onRetry}
          data-testid="error-retry"
        >
          {retryLabel}
        </button>
      )}
    </div>
  );
}
